import { ReactNode, useEffect, useMemo, useRef } from 'react'
import {
  useFloating,
  autoUpdate,
  useInteractions,
  useDismiss,
  useRole,
  FloatingPortal,
  Placement
} from '@floating-ui/react'
import { offset, shift, flip } from '@floating-ui/dom'
import { Box } from '@mui/material'
import { theme } from 'theme'
import React from 'react'
import { FloatingArrow, arrow } from '@floating-ui/react'
import Logger from 'services/Logger'
import i18n from 'i18n'

interface TFloatingPopoverProps {
  content: ReactNode
  anchorElement: HTMLElement | null
  open: boolean
  onClose: () => void
  id?: string
  closeOnTouchOutside?: boolean
  onOutsideClick?: () => void
  defaultPlacement?: 'left' | 'right' | 'top' | 'bottom'
  fallbackPlacements?: Placement[]
  rightClickClose?: boolean
}

const TFloatingPopover = (props: TFloatingPopoverProps) => {
  const {
    open,
    onClose,
    anchorElement,
    content,
    closeOnTouchOutside: closeOnTouchOutside = false,
    onOutsideClick
  } = props

  const arrowRef = useRef(null)
  const lastPositionRef = useRef<{ x: number; y: number } | null>(null)

  const { x, y, strategy, refs, context, placement } = useFloating({
    open,
    onOpenChange: onClose,
    elements: { reference: anchorElement },
    placement:
      props.defaultPlacement ?? (i18n.dir() === 'ltr' ? 'right' : 'left'),
    middleware: [
      offset({ mainAxis: 10, crossAxis: 0 }),
      flip({
        fallbackStrategy: 'initialPlacement',
        padding: 8,
        fallbackPlacements: props.fallbackPlacements ?? [
          props.defaultPlacement ?? (i18n.dir() === 'ltr' ? 'right' : 'left'),
          i18n.dir() === 'ltr' ? 'right' : 'left',
          'top'
        ]
      }),
      shift({ padding: 8 }),
      arrow({
        element: arrowRef
      })
    ],
    whileElementsMounted: autoUpdate
  })

  // Preserve last position while open
  useEffect(() => {
    if (
      open &&
      anchorElement &&
      typeof x === 'number' &&
      typeof y === 'number'
    ) {
      lastPositionRef.current = { x, y }
    }
    if (!open) {
      lastPositionRef.current = null
    }
  }, [open, anchorElement, x, y])

  const { getFloatingProps } = useInteractions([
    useDismiss(context, {
      outsidePress: (event) => {
        return closeOnTouchOutside
      }
    }),
    useRole(context, { role: 'tooltip' })
  ])

  const isCutOff = x < 0

  const isAnchorValid = anchorElement && document.body.contains(anchorElement)

  const popoverStyle = useMemo(() => {
    const isTopPlacement = placement.startsWith('top')

    // Use last known position if anchor is not valid
    if (!isAnchorValid && lastPositionRef.current) {
      return {
        top: lastPositionRef.current.y,
        left: lastPositionRef.current.x,
        position: 'fixed',
        borderRadius: '16px',
        boxShadow:
          '0px 4px 20px rgba(0, 0, 0, 0.15), 0px 8px 40px rgba(0, 0, 0, 0.3)',
        padding: theme.spacing(2, 0),
        backgroundColor: 'rgba(245, 245, 245, 1)',
        zIndex: 1300
      }
    }

    if (isCutOff) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        position: 'fixed',
        borderRadius: '16px',
        boxShadow:
          '0px 4px 20px rgba(0, 0, 0, 0.15), 0px 8px 40px rgba(0, 0, 0, 0.3)',
        padding: theme.spacing(2, 0),
        backgroundColor: 'rgba(245, 245, 245, 1)',
        zIndex: 1300
      }
    }

    return {
      top: y ?? 0,
      left: x ?? 0,
      position: strategy,
      borderRadius: '16px',
      boxShadow:
        '0px 4px 20px rgba(0, 0, 0, 0.15), 0px 8px 40px rgba(0, 0, 0, 0.3)',
      padding: theme.spacing(2, 0),
      backgroundColor: 'rgba(245, 245, 245, 1)',
      zIndex: 1300,
      transform: isTopPlacement ? 'translateY(-5px)' : 'translateY(5px)'
    }
  }, [x, y, strategy, placement, anchorElement, isCutOff, isAnchorValid])

  if (!open) return null

  return (
    <FloatingPortal>
      <div
        style={{
          display: closeOnTouchOutside || onOutsideClick ? 'flex' : 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.0)',
          zIndex: closeOnTouchOutside ? 1299 : onOutsideClick ? 999 : 1
        }}
        onContextMenu={(e) => {
          e.preventDefault()
          e.stopPropagation()

          if (props.rightClickClose && onOutsideClick) {
            onOutsideClick()
          }
        }}
        onClick={
          closeOnTouchOutside ? (onOutsideClick ?? onClose) : onOutsideClick
        }
      />
      <Box
        ref={refs.setFloating}
        sx={{ ...popoverStyle, border: '1px solid #F5F5F5' }}
        {...getFloatingProps()}
      >
        {content}

        {!isCutOff && isAnchorValid && (
          <FloatingArrow
            width={20}
            height={12}
            stroke="#F5F5F5"
            strokeWidth={1}
            style={{ marginTop: '-4px' }}
            fill="rgba(245, 245, 245, 1)"
            ref={arrowRef}
            context={context}
          />
        )}
      </Box>
    </FloatingPortal>
  )
}

export default React.memo(TFloatingPopover)
